// @ts-nocheck
/**
 * Canvas 类型定义 — ReactFlow 节点/边的类型化包装
 *
 * 对齐 SessionCanvas.jsx 的 nodeTypes 注册表和节点 data 结构
 */

import type { Node, Edge, NodeProps } from 'reactflow';
import type { Block } from './block';
import type {
  BlockType,
  BlockContentMap,
  PDFSnippetContent,
  QANormalContent,
  QAQuickScanContent,
  QADeepDiveContent,
  QACriticalContent,
  QASocraticContent,
  QADeepReviewContent,
  StickyNoteContent,
  PDFViewerContent,
} from './block';

// ============================================================
// 节点 data 结构 — 每种节点组件接收的 data prop
// ============================================================

/** 所有节点共享的基础 data 字段 */
interface BaseNodeData {
  blockId: string;
  page?: number;
  label: string;
}

/** PDFSnippetNode 的 data */
export interface PDFSnippetNodeData extends BaseNodeData {
  type: 'pdf_snippet';
  content: PDFSnippetContent;
}

/** QANode 的 data — 复用同一组件，通过 type 区分 5 种模式 */
export interface QANodeData extends BaseNodeData {
  type: 'qa_normal' | 'qa_quick_scan' | 'qa_deep_dive' | 'qa_critical' | 'qa_socratic';
  content: QANormalContent | QAQuickScanContent | QADeepDiveContent | QACriticalContent | QASocraticContent;
}

/** DeepReviewNode 的 data */
export interface DeepReviewNodeData extends BaseNodeData {
  type: 'qa_deep_review';
  content: QADeepReviewContent;
}

/** StickyNoteNode 的 data */
export interface StickyNoteNodeData extends BaseNodeData {
  type: 'sticky_note';
  content: StickyNoteContent;
}

/** PDFViewerNode 的 data */
export interface PDFViewerNodeData extends BaseNodeData {
  type: 'pdf_viewer';
  content: PDFViewerContent;
}

/** 所有节点 data 的联合类型 */
export type CanvasNodeData =
  | PDFSnippetNodeData
  | QANodeData
  | DeepReviewNodeData
  | StickyNoteNodeData
  | PDFViewerNodeData;

// ============================================================
// ReactFlow 节点/边类型化
// ============================================================

/** 类型化的 ReactFlow Node */
export type CanvasNode = Node<CanvasNodeData>;

/** 类型化的 ReactFlow Edge（带可选 label） */
export type CanvasEdge = Edge<{ label?: string; type?: string }>;

// ============================================================
// 节点类型注册表 — 对齐 SessionCanvas.jsx 的 nodeTypes
// ============================================================

/**
 * SessionCanvas.jsx 的 nodeTypes 映射：
 *
 *   pdf_snippet    → PDFSnippetNode
 *   pdf_viewer     → PDFViewerNode
 *   qa_normal      → QANode
 *   qa_quick_scan  → QANode
 *   qa_deep_dive   → QANode
 *   qa_critical    → QANode
 *   qa_socratic    → QANode
 *   qa_deep_review → DeepReviewNode
 *   sticky_note    → StickyNoteNode
 */
export type CanvasNodeType =
  | 'pdf_snippet'
  | 'pdf_viewer'
  | 'qa_normal'
  | 'qa_quick_scan'
  | 'qa_deep_dive'
  | 'qa_critical'
  | 'qa_socratic'
  | 'qa_deep_review'
  | 'sticky_note';

// ============================================================
// 节点组件 Props — 各 Node 组件的 props 类型
// ============================================================

export type PDFSnippetNodeProps = NodeProps<PDFSnippetNodeData>;
export type QANodeProps = NodeProps<QANodeData>;
export type DeepReviewNodeProps = NodeProps<DeepReviewNodeData>;
export type StickyNoteNodeProps = NodeProps<StickyNoteNodeData>;
export type PDFViewerNodeProps = NodeProps<PDFViewerNodeData>;

// ============================================================
// 节点默认尺寸 — 对齐 SessionCanvas.jsx 的 defaultSizes
// ============================================================

export const NODE_DEFAULT_SIZES: Record<CanvasNodeType, { width: number; height: number }> = {
  pdf_snippet: { width: 240, height: 160 },
  pdf_viewer: { width: 400, height: 500 },
  sticky_note: { width: 180, height: 120 },
  qa_normal: { width: 260, height: 180 },
  qa_quick_scan: { width: 260, height: 180 },
  qa_deep_dive: { width: 280, height: 200 },
  qa_deep_review: { width: 420, height: 350 },
  qa_critical: { width: 280, height: 200 },
  qa_socratic: { width: 280, height: 200 },
};

// ============================================================
// SessionCanvas 公共接口 — forwardRef 暴露的方法
// ============================================================

export interface SessionCanvasHandle {
  addBlock: (block: Block) => void;
  refresh: () => void;
}

// ============================================================
// Canvas viewport 状态
// ============================================================

export interface CanvasViewport {
  x: number;
  y: number;
  zoom: number;
}
